import { useState, useMemo, useEffect } from 'react';
import { useAppContext } from '../contexts/AppContext';
import { authenticatedFetchJSON } from '../utils/authenticatedFetch';

export interface ProjectTableData {
    id: string;
    name: string;
    displayName: string;
    customer: string;
    status: 'active' | 'inactive' | 'archived';
    stage: 'concept' | 'design' | 'prototype' | 'production' | 'maintenance';
    owner: string;
    lastModified: string;
    itemCount: number;
    created: string;
    isLoaded: boolean;
    isActive: boolean;
    fmeaStandard: string;
}

export const useProjectManagement = () => {
    const {
        backendProjects,
        projectList,
        refreshBackendProjects,
        activeProject,
        setActiveProject,
        importProjectData,
        allData,
        currentUser
    } = useAppContext();

    // Local State for Backend Source of Truth
    const [projects, setProjects] = useState<any[]>([]);

    // UI State
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState<string>('all');
    const [stageFilter, setStageFilter] = useState<string>('all');
    const [sortBy, setSortBy] = useState<'name' | 'customer' | 'modified' | 'stage'>('modified');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

    // Actions State
    const [isSaving, setIsSaving] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [loadingProject, setLoadingProject] = useState<string | null>(null);
    const [loadSuccess, setLoadSuccess] = useState<string | null>(null);
    const [isExporting, setIsExporting] = useState(false);

    // Load Projects from Backend
    const loadProjects = async () => {
        try {
            console.log('🔄 Fetching project list from backend...');
            const data = await authenticatedFetchJSON('/api/projects/');
            if (Array.isArray(data)) {
                setProjects(data);
                console.log(`✅ Loaded ${data.length} projects from backend`);
            }
        } catch (error) {
            console.error('Failed to load projects:', error);
        }
    };

    useEffect(() => {
        loadProjects();
    }, [backendProjects]);

    useEffect(() => {
        refreshBackendProjects();
    }, []);

    // Transform backend data
    const projectTableData = useMemo((): ProjectTableData[] => {
        return projects.map(p => {
            const isActive = activeProject === p.name;
            return {
                id: p.name,
                name: p.name,
                displayName: p.displayName || p.name,
                customer: p.customer || p.organization || 'Unassigned',
                status: (p.status as any) || 'active',
                stage: (p.stage as any) || 'design',
                owner: p.owner || 'Unknown',
                lastModified: p.lastModified || new Date().toISOString(),
                created: p.created || new Date().toISOString(),
                itemCount: p.itemCount || 0,
                isLoaded: projectList.some(pl => pl.name === p.name),
                isActive,
                fmeaStandard: p.fmeaStandard || 'AIAG_VDA'
            };
        });
    }, [projects, activeProject, projectList]);

    // Filters and Sorting
    const filteredAndSortedProjects = useMemo(() => {
        let filtered = projectTableData;

        if (searchQuery) {
            const q = searchQuery.toLowerCase();
            filtered = filtered.filter(p =>
                p.name.toLowerCase().includes(q) ||
                p.displayName.toLowerCase().includes(q) ||
                p.customer.toLowerCase().includes(q)
            );
        }

        if (statusFilter !== 'all') {
            filtered = filtered.filter(p => p.status === statusFilter);
        }

        if (stageFilter !== 'all') {
            filtered = filtered.filter(p => p.stage === stageFilter);
        }

        filtered.sort((a, b) => {
            let comparison = 0;
            switch (sortBy) {
                case 'name': comparison = a.name.localeCompare(b.name); break;
                case 'customer': comparison = a.customer.localeCompare(b.customer); break;
                case 'stage': comparison = a.stage.localeCompare(b.stage); break;
                case 'modified':
                    comparison = new Date(a.lastModified).getTime() - new Date(b.lastModified).getTime();
                    break;
            }
            return sortDirection === 'asc' ? comparison : -comparison;
        });

        return filtered;
    }, [projectTableData, searchQuery, statusFilter, stageFilter, sortBy, sortDirection]);

    // Handlers
    const toggleSort = (field: 'name' | 'customer' | 'modified' | 'stage') => {
        if (sortBy === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(field);
            setSortDirection('desc');
        }
    };

    const canDelete = (projectName: string): boolean => {
        if (!currentUser) return false;
        if (['admin', 'autoGainAdmin', 'clientAdmin'].includes(currentUser.role)) return true;
        const project = projectTableData.find(p => p.name === projectName);
        return !!project && project.owner === currentUser.username;
    };

    const handleLoadProject = async (projectName: string) => {
        const proj = projectTableData.find(p => p.name === projectName);
        if (proj?.isLoaded) {
            setActiveProject(projectName);
            setLoadSuccess(projectName);
            setTimeout(() => setLoadSuccess(null), 2000);
            return;
        }

        setLoadingProject(projectName);
        setLoadSuccess(null);
        try {
            const projectData = await authenticatedFetchJSON(`/api/projects/load/${encodeURIComponent(projectName)}`);
            await importProjectData(projectData, true);
            setActiveProject(projectName);
            setLoadSuccess(projectName);
            setTimeout(() => setLoadSuccess(null), 2000);
        } catch (error: any) {
            console.error("Load failed", error);
            alert("Failed to load project: " + error);
        } finally {
            setLoadingProject(null);
        }
    };

    const handleDelete = async (projectName: string) => {
        if (!canDelete(projectName)) {
            alert("You don't have permission to delete this project.");
            return;
        }
        if (!window.confirm(`Are you sure you want to delete project "${projectName}"? This action cannot be undone.`)) return;

        try {
            await authenticatedFetchJSON(`/api/projects/${encodeURIComponent(projectName)}`, { method: 'DELETE' });
            alert(`Project "${projectName}" deleted successfully.`);
            refreshBackendProjects();
            if (activeProject === projectName) setActiveProject('all');
        } catch (error) {
            console.error("Delete failed", error);
            alert("Error deleting project.");
        }
    };

    const handleExport = async (projectName: string) => {
        setIsExporting(true);
        try {
            const projectData = await authenticatedFetchJSON(`/api/projects/load/${encodeURIComponent(projectName)}`);
            const dataStr = JSON.stringify(projectData, null, 2);
            const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);
            const linkElement = document.createElement('a');
            linkElement.setAttribute('href', dataUri);
            linkElement.setAttribute('download', `${projectName}.json`);
            linkElement.click();
        } catch (error) {
            console.error("Export failed", error);
            alert("Export failed: " + error);
        } finally {
            setIsExporting(false);
        }
    };

    const handleSaveActiveProject = async () => {
        if (!activeProject || activeProject === 'all') return;
        setIsSaving(true);
        setSaveSuccess(false);
        try {
            await authenticatedFetchJSON('/api/projects/save', {
                method: 'POST',
                body: JSON.stringify(allData)
            });
            setSaveSuccess(true);
            setTimeout(() => setSaveSuccess(false), 3000);
            refreshBackendProjects();
        } catch (error: any) {
            console.error('Save failed:', error);
            alert(`❌ Error saving project: ${error.message || error}`);
        } finally {
            setIsSaving(false);
        }
    };

    return {
        // State
        projects,
        projectTableData,
        filteredAndSortedProjects,
        searchQuery, setSearchQuery,
        statusFilter, setStatusFilter,
        stageFilter, setStageFilter,
        sortBy, toggleSort,
        sortDirection,
        isSaving, saveSuccess,
        loadingProject, loadSuccess,
        isActiveProject: activeProject !== 'all' && !!activeProject,
        isExporting,

        // Actions
        handleLoadProject,
        handleDelete,
        handleExport,
        handleSaveActiveProject,
        refreshBackendProjects,
        canDelete,
        allData,
        activeProject
    };
};
